import { ReactNode, createContext, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { invitationShowOptions } from '$api-client/@tanstack/react-query.gen'
import { LoaderIcon } from '$components'
import { InvalidInvitationError } from './invalid-invitation-error'
import { Invitation } from '$api-client/types.gen'

export type InvitationContextType = {
  invitation: Invitation
  token: string
  isLoading: boolean
}

export const InvitationContext = createContext<InvitationContextType | undefined>(undefined)

export type InvitationProviderProps = {
  children: ReactNode
}

export function InvitationProvider({ children }: InvitationProviderProps) {
  const { token } = useParams()

  const {
    data: invitation,
    isLoading: isInvitationLoading,
  } = useQuery({
    ...invitationShowOptions({
      path: {
        token: token!,
      },
    }),
    retry: false,
  })

  if (isInvitationLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <LoaderIcon className="size-6" />
      </div>
    )
  }

  if (!invitation) {
    return <InvalidInvitationError />
  }

  const context: InvitationContextType = {
    invitation,
    token: token!,
    isLoading: isInvitationLoading,
  }

  return (
    <InvitationContext.Provider value={context}>
      {children}
    </InvitationContext.Provider>
  )
}

export function useInvitation() {
  const context = useContext(InvitationContext)

  if (context === undefined) {
    throw new Error('useInvitation must be used within an InvitationProvider')
  }

  return context
}
